/**
 * Fichadas → Quilmes: las marcas que el destino rechazó y el horario del
 * aviso.
 *
 * El portal es el **sistema consumidor** de la integración de fichadas: no
 * replica marcas ni decide reintentos, solo lee lo que esa integración dejó
 * rechazado y avisa por correo a quien tiene que corregir el alta.
 *
 * Lo que la pantalla tiene que dejar claro es el **plazo**. La integración
 * reintenta sola mientras la fichada siga dentro de su ventana; pasado ese
 * plazo la marca deja de consultarse y recuperarla exige una corrida manual.
 * Por eso la tabla ordena por lo que queda y no por lo que pasó.
 */

import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Clock, Loader2, RefreshCw, Save } from 'lucide-react';

import { mensajeDe } from '../../api';
import { Aviso } from '../../components/Aviso';
import { Paginacion } from '../../components/Paginacion';
import { EnvoltorioTabla, FilaVacia } from '../../components/Tabla';
import { fechaHoraLegible } from '../../lib/fechas';
import { paginaValida } from '../../lib/paginacion';
import {
  guardarProgramacion,
  leerProgramacion,
  listarRechazos,
  type ProgramacionQuilmes,
  type RespuestaRechazos,
} from './api';

/** Veinte por página, como pidió RRHH: es lo que entra sin scrollear. */
const POR_PAGINA = 20;

export function VistaQuilmes(): React.JSX.Element {
  const [datos, setDatos] = useState<RespuestaRechazos | null>(null);
  const [programacion, setProgramacion] = useState<ProgramacionQuilmes | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [pagina, setPagina] = useState(1);

  const [activa, setActiva] = useState(false);
  const [horarios, setHorarios] = useState('');
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async (): Promise<void> => {
    setCargando(true);
    try {
      /*
       * Las dos juntas: si la conexión a la base de la integración falla, el
       * horario igual se tiene que poder mirar y cambiar.
       */
      const [prog, rechazos] = await Promise.allSettled([leerProgramacion(), listarRechazos()]);

      if (prog.status === 'fulfilled') {
        setProgramacion(prog.value);
        setActiva(prog.value.activa);
        setHorarios(prog.value.horarios.join(', '));
      }
      if (rechazos.status === 'fulfilled') setDatos(rechazos.value);

      const fallo = [prog, rechazos].find((r) => r.status === 'rejected');
      setError(fallo === undefined ? '' : mensajeDe(fallo.reason));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function guardar(): Promise<void> {
    setGuardando(true);
    try {
      const r = await guardarProgramacion(activa, horarios);
      setHorarios(r.horarios.join(', '));
      setAviso(
        r.activa
          ? `El aviso queda activo a las ${r.horarios.join(' y ')}.`
          : 'El aviso queda apagado: no se manda ningún correo.',
      );
      setError('');
      await cargar();
    } catch (err) {
      setError(mensajeDe(err));
      setAviso('');
    } finally {
      setGuardando(false);
    }
  }

  const rechazos = datos?.rechazos ?? [];
  const p = paginaValida(pagina, rechazos.length, POR_PAGINA);
  const visibles = rechazos.slice((p - 1) * POR_PAGINA, p * POR_PAGINA);
  const colaboradores = new Set(rechazos.map((r) => r.dni)).size;

  return (
    <div className="quilmes">
      <header className="novedades-encabezado">
        <div>
          <h1>Fichadas VISMA → Quilmes</h1>
          <p className="bajada">
            Marcas que el destino rechazó y todavía no se registraron. Se reintentan solas mientras
            la fichada siga dentro de la ventana; después hay que pedir una corrida manual.
          </p>
        </div>
      </header>

      {error !== '' && <Aviso tono="error">{error}</Aviso>}
      {aviso !== '' && <Aviso tono="ok">{aviso}</Aviso>}

      {/*
        Que el horario esté activo no alcanza: sin relay o sin destinatarios
        no sale nada, y una pantalla que diga "activo" estaría mintiendo.
      */}
      {programacion !== null && programacion.activa && !programacion.correoConfigurado && (
        <Aviso tono="atencion">
          El aviso está activo pero no hay correo configurado: falta el relay (SMTP) o la lista de
          destinatarios. Mientras tanto no se manda nada.
        </Aviso>
      )}
      {programacion !== null && programacion.activa && !programacion.programadorActivo && (
        <Aviso tono="atencion">
          Esta instancia del portal no es la que manda el aviso. El horario queda guardado, pero
          quien dispara es la instancia con `QUILMES_PROGRAMADOR=true`.
        </Aviso>
      )}
      {datos !== null && datos.perdidas > 0 && (
        <Aviso tono="atencion">
          {`${String(datos.perdidas)} marca(s) ya salieron de la ventana de ${String(datos.ventanaDias)} día(s): la integración no las vuelve a intentar sola. Para recuperarlas hay que pedir una corrida manual con fechas explícitas.`}
        </Aviso>
      )}

      <section className="tarjeta">
        <div className="tarjeta-titulo">
          <h2>Horario del aviso</h2>
          <span className="nota">
            {programacion === null
              ? ''
              : programacion.destinatarios.length === 0
                ? 'Sin destinatarios configurados'
                : `A ${programacion.destinatarios.join(', ')}`}
          </span>
        </div>

        <div className="banda">
          <label className="campo campo--casilla">
            <input
              type="checkbox"
              checked={activa}
              onChange={(ev) => setActiva(ev.target.checked)}
            />
            <span>
              Avisar por correo
              <span className="sub">
                Se manda una sola vez por marca: lo que ya se avisó no vuelve a salir.
              </span>
            </span>
          </label>

          <label className="campo">
            Horarios
            <input
              type="text"
              placeholder="09:00, 15:00"
              value={horarios}
              onChange={(ev) => setHorarios(ev.target.value)}
            />
          </label>

          <button type="button" className="primaria" disabled={guardando} onClick={() => void guardar()}>
            {guardando ? (
              <Loader2 className="girando" size={16} aria-hidden="true" />
            ) : (
              <Save size={16} aria-hidden="true" />
            )}
            Guardar
          </button>

          {programacion?.ultimoEnvioEn != null && (
            <span className="sello-consulta" data-tono="neutro">
              <Clock size={13} aria-hidden="true" />
              {`Último: ${fechaHoraLegible(programacion.ultimoEnvioEn)} · ${programacion.ultimoResultado ?? ''}`}
            </span>
          )}
        </div>
      </section>

      <section className="tarjeta tarjeta--tabla">
        <div className="tarjeta-titulo">
          <h2>Marcas rechazadas</h2>
          <div className="acciones">
            <span className="nota">
              {datos === null
                ? ''
                : `${String(rechazos.length)} marca(s) de ${String(colaboradores)} colaborador(es), dentro de la ventana de ${String(datos.ventanaDias)} día(s)`}
            </span>
            <button type="button" className="plano" disabled={cargando} onClick={() => void cargar()}>
              <RefreshCw size={15} aria-hidden="true" />
              Actualizar
            </button>
          </div>
        </div>

        <EnvoltorioTabla
          pie={
            <Paginacion
              total={rechazos.length}
              pagina={p}
              tamanio={POR_PAGINA}
              onCambiar={setPagina}
              etiqueta="marcas"
            />
          }
        >
          <table className="tabla">
            <thead>
              <tr>
                <th scope="col" className="num">DNI</th>
                <th scope="col" className="num">Fichada</th>
                <th scope="col">Tipo</th>
                <th scope="col">Grupo</th>
                <th scope="col">Qué hay que hacer</th>
                <th scope="col" className="num">Intentos</th>
                <th scope="col" className="num">Falla hace</th>
                <th scope="col" className="num">Le queda</th>
                <th scope="col">Avisado</th>
              </tr>
            </thead>
            <tbody>
              {cargando && (
                <FilaVacia columnas={9}>
                  <Loader2 className="girando" size={16} aria-hidden="true" /> Cargando…
                </FilaVacia>
              )}
              {!cargando && rechazos.length === 0 && (
                <FilaVacia columnas={9}>
                  No hay marcas rechazadas pendientes dentro de la ventana.
                </FilaVacia>
              )}
              {!cargando &&
                visibles.map((r) => (
                  <tr key={r.clave}>
                    <td className="num">{r.dni}</td>
                    <td className="num">{r.fechaMarca === null ? '—' : fechaHoraLegible(r.fechaMarca)}</td>
                    <td><span className="chip">{r.tipo}</span></td>
                    <td>{r.grupo}</td>
                    <td>
                      {r.motivo}
                      {r.codigoError !== '' && <span className="sub">{`código ${r.codigoError}`}</span>}
                    </td>
                    <td className="num">{r.intentos}</td>
                    <td className="num">{r.horasFallando === null ? '—' : `${String(r.horasFallando)} h`}</td>
                    {/* El plazo es lo accionable: cuando se agota, la marca
                        se pierde y ya no alcanza con corregir el alta. */}
                    <td className="num"><Plazo horas={r.horasQueLeQuedan} /></td>
                    <td>
                      {r.notificada === null ? (
                        <span className="tenue">—</span>
                      ) : (
                        fechaHoraLegible(r.notificada)
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </EnvoltorioTabla>
      </section>
    </div>
  );
}

/** Cuánto le queda a la marca, y si ya se le pasó. */
function Plazo({ horas }: { readonly horas: number | null }): React.JSX.Element {
  if (horas === null) return <span className="tenue">—</span>;
  if (horas <= 0) {
    return (
      <span className="plazo plazo--vencido">
        <AlertTriangle size={13} aria-hidden="true" />
        vencido
      </span>
    );
  }
  // Menos de un turno de trabajo: ya no se corrige "mañana".
  const apremia = horas <= 8;
  return (
    <span className={apremia ? 'plazo plazo--apremia' : 'plazo'}>{`${String(horas)} h`}</span>
  );
}
